import React, { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface GoogleSignInProps {
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  size?: 'large' | 'medium' | 'small';
  width?: string;
  useCustomButton?: boolean;
  buttonTheme?: 'light' | 'dark';
}

const GoogleGIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const GoogleSignIn: React.FC<GoogleSignInProps> = ({
  text = 'signin_with',
  size = 'large',
  width = '100%',
  useCustomButton = false,
  buttonTheme = 'dark',
}) => {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleCredentialResponse = useCallback(async (response: any) => {
    try {
      await googleLogin(response.credential);
      const from = (location.state as any)?.from || '/';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      toast.error(error.response?.data?.error || 'Failed to sign in with Google');
    }
  }, [googleLogin, navigate, location.state]);

  /** Load gsi only here (not in index.html) so it does not race Google Maps on Home. */
  const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

  useEffect(() => {
    let cancelled = false;
    /** Browser timers are numeric IDs; avoid Node `Timer` vs DOM `number` clash from @types/node. */
    let pollId: number | undefined;

    const tryInit = () => {
      if (cancelled) return true;
      const g = (window as unknown as { google?: { accounts?: { id?: { initialize: (o: object) => void; renderButton: (el: HTMLElement, o: object) => void } } } }).google;
      if (!g?.accounts?.id) return false;
      g.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        callback: handleCredentialResponse,
      });
      if (!useCustomButton && buttonRef.current) {
        g.accounts.id.renderButton(buttonRef.current, { theme: 'outline', size, text, width });
      }
      return true;
    };

    if (tryInit()) {
      return () => {
        cancelled = true;
      };
    }

    let script = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.src = GSI_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const onLoad = () => {
      if (!cancelled) tryInit();
    };
    script.addEventListener('load', onLoad);

    let n = 0;
    pollId = window.setInterval(() => {
      if (cancelled || tryInit() || ++n > 150) {
        if (pollId != null) window.clearInterval(pollId);
      }
    }, 100);

    return () => {
      cancelled = true;
      script?.removeEventListener('load', onLoad);
      if (pollId != null) window.clearInterval(pollId);
    };
  }, [text, size, width, useCustomButton, handleCredentialResponse]);

  const triggerGoogle = () => {
    if (useCustomButton && (window as any).google?.accounts?.id) {
      (window as any).google.accounts.id.prompt();
      return;
    }
    (buttonRef.current?.querySelector('div[role="button"]') as HTMLElement)?.click();
  };

  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return <div className="text-sm text-white/80 text-center py-2">Google Sign-In not configured</div>;
  }

  if (useCustomButton) {
    const isLight = buttonTheme === 'light';
    return (
      <button
        type="button"
        onClick={triggerGoogle}
        className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-medium text-base border transition-colors ${
          isLight ? 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50 focus:ring-2 focus:ring-green-500 focus:ring-offset-2' : 'bg-[#303030] hover:bg-[#3d3d3d] text-white border-white/10'
        }`}
      >
        <GoogleGIcon />
        <span>Continue with Google</span>
      </button>
    );
  }

  return <div ref={buttonRef} className="w-full flex justify-center"></div>;
};

export default GoogleSignIn;

